import { type RequestWithDetails } from "@/hooks/useRequests";
import { type ReportAggregation } from "@glorify-llc/accounting-core";
import { buildReportSummaryItem } from "@/lib/metrics/metric-registry";
import { isRequestOpenStatus } from "@/lib/request-display";

export type OpenRequestsByAgeContext = {
  asOf: Date;
  requests: RequestWithDetails[];
};

const MS_PER_DAY = 1000 * 60 * 60 * 24;

/**
 * Normalize a free-form status to the canonical lowercase form so the
 * "open" filter matches "Open", " OPEN ", or "in_progress" alike.
 * The DB enum is lowercase, but defensive coercion protects against
 * legacy rows or future schema drift.
 */
const normalizeStatus = (status: unknown): string => {
  return typeof status === "string" ? status.trim().toLowerCase() : "";
};

const buildRequesterName = (request: RequestWithDetails): string => {
  if (!request.person) return "Unassigned";
  const name = `${request.person.first_name || ""} ${request.person.last_name || ""}`.trim();
  return name || "Unassigned";
};

/**
 * Compute days between createdAt and asOf, clamped to a non-negative
 * integer. Future-dated `created_at` (clock skew) would otherwise yield
 * a negative `daysOpen` which sorts above legitimate aged rows.
 */
const computeDaysOpen = (createdAt: Date, asOf: Date): number => {
  const diff = asOf.getTime() - createdAt.getTime();
  if (!Number.isFinite(diff)) return 0;
  const days = Math.floor(diff / MS_PER_DAY);
  return days < 0 ? 0 : days;
};

const isFiniteDate = (date: Date | null): date is Date =>
  date !== null && Number.isFinite(date.getTime());

export function buildOpenRequestsByAgeAggregation({
  asOf,
  requests,
}: OpenRequestsByAgeContext): ReportAggregation {
  const safeRequests = Array.isArray(requests) ? requests : [];

  const openRequests = safeRequests.filter((request) =>
    isRequestOpenStatus(normalizeStatus(request?.status))
  );

  const rows = openRequests
    .map((request) => {
      const rawCreated = request.created_at;
      const createdAt = rawCreated ? new Date(rawCreated) : null;
      const daysOpen = isFiniteDate(createdAt) ? computeDaysOpen(createdAt, asOf) : null;
      const rawTitle = typeof request.title === "string" ? request.title.trim() : "";
      const requestType =
        typeof request.type === "string" ? request.type.trim().toLowerCase() : null;

      return {
        id: request.id,
        label: rawTitle || "Request",
        requester: buildRequesterName(request),
        type: requestType || null,
        status: request.status,
        priority: request.priority,
        daysOpen,
        createdDate: rawCreated || null,
        dueDate: request.due_date || null,
        entityType: "request" as const,
        drilldownUrl: `/requests/${request.id}${requestType === "reimbursement" ? "?from=finance" : ""}`,
      };
    })
    // Most-aged first. Rows with unparseable / missing `created_at` (daysOpen
    // null) sink to the bottom rather than mingling with daysOpen=0; ties
    // broken by id so refetches don't reshuffle the list.
    .sort((a, b) => {
      const av = a.daysOpen ?? -1;
      const bv = b.daysOpen ?? -1;
      if (av !== bv) return bv - av;
      if (a.id < b.id) return -1;
      if (a.id > b.id) return 1;
      return 0;
    });

  return {
    asOf,
    summary: [buildReportSummaryItem("report.open_requests.open_count", rows.length)],
    rows,
  };
}
